import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useScript } from '@/hooks/use-script';
import { useAdUrl } from '@/hooks/use-consent';

interface AdSectionProps {
  /** Raw tag URL as stored in config/ads.ts or the CMS (may contain ${GDPR} and GAM macros). */
  adUrl: string;
  sectionId: string;
  className?: string;
  /** When true, the ad script is deferred until the container is near the viewport */
  lazy?: boolean;
}

/**
 * Inner component — only mounted when we're ready to load the ad.
 * Keeping it separate means useScript is never called before the
 * IntersectionObserver fires, avoiding unnecessary network requests.
 */
interface AdLoaderProps {
  adUrl: string;
  sectionId: string;
  containerRef: React.RefObject<HTMLDivElement>;
  onReady: () => void;
  onError: () => void;
}

const AdLoader: React.FC<AdLoaderProps> = ({ adUrl, sectionId, containerRef, onReady, onError }) => {
  const { ready, error } = useScript({
    src: adUrl,
    appendTo: containerRef,
    id: `ad-script-${sectionId}`,
    async: true,
  });

  useEffect(() => {
    if (ready) onReady();
  }, [ready, onReady]);

  useEffect(() => {
    if (error) onError();
  }, [error, onError]);

  return null;
};

const AdSection: React.FC<AdSectionProps> = ({ adUrl, sectionId, className = '', lazy = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // null until Zaraz consent state is known; changes if the visitor updates their choices
  const resolvedUrl = useAdUrl(adUrl);
  // Non-lazy ads start loading immediately; lazy ads wait for IntersectionObserver
  const [shouldLoad, setShouldLoad] = useState(!lazy);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Wire up IntersectionObserver for lazy ads
  useEffect(() => {
    if (!lazy || shouldLoad) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      // Start loading 300px before the element enters the viewport
      { rootMargin: '150px 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy, shouldLoad]);

  const handleReady = useCallback(() => setIsReady(true), []);
  const handleError = useCallback(() => setHasError(true), []);

  return (
    <div
      ref={containerRef}
      className={`relative embedded-ad-container aspect-[4/3] bg-white dark:bg-slate-800/50 rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 overflow-hidden border border-slate-200/50 dark:border-white/10 backdrop-blur-xl sm:max-w-3/4 mx-auto transition-opacity duration-700 ${
        isReady ? 'opacity-100' : 'opacity-0'
      } ${className}`}
    >
      {shouldLoad && !isReady && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-slate-500 dark:text-slate-400">Loading ad...</div>
        </div>
      )}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-red-500">Failed to load ad</div>
        </div>
      )}
      {shouldLoad && resolvedUrl && (
        <AdLoader
          key={resolvedUrl}
          adUrl={resolvedUrl}
          sectionId={sectionId}
          containerRef={containerRef}
          onReady={handleReady}
          onError={handleError}
        />
      )}
    </div>
  );
};

export default AdSection;
